import { useContext, useMemo, useState } from 'react';
import { Breadcrumb, Button, Card, Col, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import { AppContext } from '../../../common';
import BackButton from '../../../common/filter-table/BackButton';
import { Private_Routes } from '../../../constants';
import { BonusAssignmentModel } from '../models/bonusAssignmentModel';
import BonusTable from '../components/BonusTable';
import { DeleteBonusModal, DeleteBonusModalProps } from '../components/modals/DeleteBonusModal';
import { useManageBonus } from '../hooks/useManageBonus';
import { BonusTabs } from '../components/tabs-panel/BonusTabs';

export const ManageBonus = () => {
    const { startDateScheduling, endDateScheduling, selectedEmployees, langResources, activeSkinConfig } =
        useContext(AppContext);

    const { tab } = useParams<{ tab: string }>();

    const params = useMemo(
        () => ({
            employeeIds: selectedEmployees.map((e) => Number(e.id)),
            endDate: `${endDateScheduling}T00:00:00`,
            startDate: `${startDateScheduling}T00:00:00`,
        }),
        [selectedEmployees, startDateScheduling, endDateScheduling]
    );

    const { bonusAssignments, isLoading, error, refetch, deleteBonusAssignments, isDeleting } =
        useManageBonus(params);

    const [selectedList, setSelectedList] = useState<BonusAssignmentModel[]>([]);
    const [searchEmployee, setSearchEmployee] = useState('');
    const [searchStartDate, setSearchStartDate] = useState(startDateScheduling);
    const [searchEndDate, setSearchEndDate] = useState(endDateScheduling);

    const [deleteBonusModal, setDeleteBonusModal] = useState<DeleteBonusModalProps>(
        {} as DeleteBonusModalProps
    );

    const handleChangeSelected = (item: BonusAssignmentModel) => {
        setSelectedList((prevSelectedList) => {
            const updatedList = new Set(prevSelectedList);
            if (updatedList.has(item)) {
                updatedList.delete(item);
            } else {
                updatedList.add(item);
            }

            return Array.from(updatedList);
        });
    };

    const filteredBonusAssignments = useMemo(() => {
        return bonusAssignments.filter((item) => {
            if (tab === 'employee') {
                return (
                    `${item.employee.firstName} ${item.employee.lastName}`
                        .toLowerCase()
                        .includes(searchEmployee.toLowerCase()) ||
                    item.employee.id.toString().includes(searchEmployee)
                );
            }
            return true;
        });
    }, [bonusAssignments, tab, searchEmployee]);

    const groupedData = useMemo(() => {
        if (!filteredBonusAssignments.length || !tab) return {};

        let result: Record<string, BonusAssignmentModel[]> = {};
        switch (tab) {
            case 'employee':
                result = filteredBonusAssignments.reduce<
                    Record<string, BonusAssignmentModel[]>
                >((acc, item) => {
                    const key = `${item.employee.id}`;
                    if (!acc[key]) acc[key] = [];
                    acc[key].push(item);
                    return acc;
                }, {});
                break;

            case 'date':
                result = filteredBonusAssignments
                    .filter((item) => {
                        const itemDate = item.date.split('T')[0];
                        if (searchStartDate && searchEndDate) {
                            return itemDate >= searchStartDate && itemDate <= searchEndDate;
                        }
                        if (searchStartDate) {
                            return itemDate >= searchStartDate;
                        }
                        if (searchEndDate) {
                            return itemDate <= searchEndDate;
                        }
                        return true;
                    })
                    .reduce<Record<string, BonusAssignmentModel[]>>(
                        (acc, item) => {
                            const key = item.date.split('T')[0];
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(item);
                            return acc;
                        },
                        {}
                    );
                break;

            default:
                console.warn(`Tipo de agrupamiento "${tab}" no válido`);
                break;
        }

        return result;
    }, [filteredBonusAssignments, tab, searchStartDate, searchEndDate]);

    const onCloseDeleteBonusModal = () => {
        setDeleteBonusModal((prev) => ({ ...prev, isOpen: false }));
        setSelectedList([]);
    };
    
    const openDeleteBonusModal = () => {
        setDeleteBonusModal({
            isOpen: true,
            onClose: onCloseDeleteBonusModal,
            reHidrate: refetch,
            data: selectedList,
            deleteBonusAssignments,
            isDeleting,
            setSelectedList
        });
    };
    
    const handleSelectAllItems = (isChecked: boolean) => {
        if (isChecked) {
            setSelectedList(filteredBonusAssignments);
        } else {
            setSelectedList([]);
        }
    };

    const areAllSelected =
        filteredBonusAssignments.length > 0 &&
        filteredBonusAssignments.length === selectedList.length;
    return (
        <>
            {deleteBonusModal.isOpen && <DeleteBonusModal {...deleteBonusModal} />}

            <div className={`main-content ${activeSkinConfig.rootClass}`}>
                <div className='page-content'>
                    <div className='container-fluid'>
                        <div className='row '>
                            <div className='col-12 p-3 py-0'>
                                <Helmet>
                                    <title>{langResources['bonus']['manage']['page.title']}</title>
                                </Helmet>

                                <Breadcrumb>
                                    <Breadcrumb.Item href={'/'}>
                                        {langResources['bonus']['manage']['breadcrumb.home']}
                                    </Breadcrumb.Item>
                                    <Breadcrumb.Item href={Private_Routes.HOME}>
                                        {langResources['bonus']['manage']['breadcrumb.title']}
                                    </Breadcrumb.Item>
                                </Breadcrumb>

                                <Row>
                                    <Col sm={12}>
                                        <Card>
                                            <Card.Body>
                                                <div className='d-flex justify-content-between flex-wrap'>
                                                    <div className='mb-3'>
                                                        <BackButton to={Private_Routes.HOME} />
                                                    </div>

                                                    <div className='mb-3'>
                                                        <h4 className='card-title'>
                                                            {langResources['bonus']['manage']['page.title']}
                                                        </h4>
                                                    </div>

                                                    <div>
                                                        <Button
                                                            variant='outline-info'
                                                            className='me-2'
                                                            disabled={filteredBonusAssignments.length === 0}
                                                            onClick={() => handleSelectAllItems(!areAllSelected)}
                                                        >
                                                            <i className={areAllSelected ? 'ri-checkbox-multiple-blank-line' : 'ri-checkbox-multiple-line'}></i>{' '}
                                                            {areAllSelected ? langResources['bonus']['manage']['deselect.all.button'] : langResources['bonus']['manage']['select.all.button']}
                                                        </Button>
                                                        <Button
                                                            variant='danger'
                                                            disabled={
                                                                selectedList.length === 0
                                                            }
                                                            onClick={openDeleteBonusModal}
                                                        >
                                                            <i className='ri-delete-bin-line'></i>{' '}
                                                            {langResources['bonus']['manage']['delete.button']}
                                                        </Button>
                                                    </div>
                                                </div>

                                                <BonusTabs 
                                                    activeTab={tab || 'employee'} 
                                                    setSelectedList={setSelectedList} 
                                                />

                                                {tab === 'employee' && (
                                                    <div className='mb-3'>
                                                        <input
                                                            type='text'
                                                            className='form-control'
                                                            placeholder={langResources['bonus']['manage']['search.placeholder'] || 'Search by employee name or ID'}
                                                            value={searchEmployee}
                                                            onChange={(e) => setSearchEmployee(e.target.value)}
                                                        />
                                                    </div>
                                                )}

                                                {tab === 'date' && (
                                                    <div className='row mb-3'>
                                                        <div className='col-md-6'>
                                                            <label className='form-label'>
                                                                {langResources['filter.modal.select.employees.dates.starDate'] || 'Start Date'}
                                                            </label>
                                                            <input
                                                                type='date'
                                                                className='form-control'
                                                                min={startDateScheduling}
                                                                max={endDateScheduling}
                                                                value={searchStartDate}
                                                                onChange={(e) => setSearchStartDate(e.target.value)}
                                                            />
                                                        </div>
                                                        <div className='col-md-6'>
                                                            <label className='form-label'>
                                                                {langResources['filter.modal.select.employees.dates.endDate'] || 'End Date'}
                                                            </label>
                                                            <input
                                                                type='date'
                                                                className='form-control'
                                                                min={startDateScheduling}
                                                                max={endDateScheduling}
                                                                value={searchEndDate}
                                                                onChange={(e) => setSearchEndDate(e.target.value)}
                                                            />      
                                                        </div>
                                                    </div>
                                                )}

                                                <SimpleBar
                                                    style={{
                                                        maxHeight: '75vh',
                                                        overflowX: 'auto',
                                                    }}
                                                >
                                                    {isLoading ? (
                                                        <div className='d-flex justify-content-center align-items-center'>
                                                            <div
                                                                className='spinner-border text-primary'
                                                                role='status'
                                                            >
                                                                <span className='sr-only'>
                                                                    {langResources['bonus']['manage']['loading']}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ) : error ? (
                                                        <div className='alert alert-danger'>
                                                            {error instanceof Error
                                                                ? error.message
                                                                : langResources['bonus']['manage']['error.message']}
                                                        </div>
                                                    ) : (
                                                        <BonusTable
                                                            data={groupedData}
                                                            selectedList={selectedList}
                                                            handleChangeSelected={
                                                                handleChangeSelected
                                                            }
                                                            groupBy={tab as 'employee' | 'date'}
                                                        />
                                                    )}
                                                </SimpleBar>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};